import React, { useEffect, useRef, useState } from 'react';

/**
 * 八力雷达图 —— Page 2 左侧。
 *
 * 入参：vm.abilities（已按 canonical 顺序排）。每项 {name, percentage}。
 * 半径轴 0-100（百分制，V5-升级规划 §7.3.3）。
 *
 * 实现历史（2026-05-23）：原本用 Recharts <RadarChart/>，但实测 print mode
 * （viewport 540px, full_page screenshot）下 Recharts 内部把 Radar polygon 的
 * scale 算成 outerRadius/2.16（grid 同心环算对到 115px, polygon 100% 半径只
 * 有 53px），跟数据严重对不上。试过 ResponsiveContainer / 固定 width /
 * outerRadius 数字 vs "78%" / 加去 PolarRadiusAxis / tickCount / key remount
 * 都不能让 polygon scale 跟 grid 同步——Recharts 2.12.0 的 polar scale 在某
 * 些 layout 路径下有 bug。
 *
 * 直接用 SVG 手画：完全可控，零 Recharts 依赖。8 轴、5 同心环、8 个标签、
 * 1 个 polygon。约 70 行实现，性能更好，截图无时序问题。
 */
const TICK_COUNT = 5; // 0 / 25 / 50 / 75 / 100 五个同心环
const PADDING_TOP_BOTTOM = 12;
const PADDING_LR = 80; // 留给标签的横向空间
const HEIGHT = 320;
const FALLBACK_WIDTH = 460;
// 雷达图最大半径上限 (px) —— 跟原 Recharts normal mode (wrap 387) 自然出来的
// outerRadius ~115 对齐, 防止 print mode (wrap 540) 整体被等比放大溢出.
const R_MAX = 118;

export default function ReportV5Radar({ abilities }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    if (!wrapRef.current) return undefined;
    const measure = () => {
      const w = wrapRef.current?.getBoundingClientRect().width || FALLBACK_WIDTH;
      setWidth(Math.max(280, Math.floor(w)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const cx = width / 2;
  const cy = HEIGHT / 2;
  const R = Math.min(
    R_MAX,
    width / 2 - PADDING_LR,
    HEIGHT / 2 - PADDING_TOP_BOTTOM,
  );

  const n = abilities.length;
  // 起始角度 -π/2（12 点钟方向），按数组顺序顺时针均分
  const angleOf = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;

  // 同心环（正八边形）— 用 5 个不同半径的多边形
  const rings = Array.from({ length: TICK_COUNT }, (_, i) => {
    const r = (R * (i + 1)) / TICK_COUNT;
    const pts = Array.from({ length: n }, (_, j) => {
      const a = angleOf(j);
      return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
    }).join(' ');
    return { r, pts };
  });

  // 从中心到每个轴端点的轴线
  const spokes = Array.from({ length: n }, (_, i) => {
    const a = angleOf(i);
    return {
      x: cx + R * Math.cos(a),
      y: cy + R * Math.sin(a),
    };
  });

  // 数据 polygon
  const dataPts = abilities
    .map((a, i) => {
      const pct = Math.max(0, Math.min(100, Number(a.percentage) || 0));
      const r = (R * pct) / 100;
      const ang = angleOf(i);
      return `${(cx + r * Math.cos(ang)).toFixed(2)},${(cy + r * Math.sin(ang)).toFixed(2)}`;
    })
    .join(' ');

  // 数据点圆点（hover 显示数值）
  const dataDots = abilities.map((a, i) => {
    const pct = Math.max(0, Math.min(100, Number(a.percentage) || 0));
    const r = (R * pct) / 100;
    const ang = angleOf(i);
    return {
      cx: cx + r * Math.cos(ang),
      cy: cy + r * Math.sin(ang),
      pct: a.percentage,
    };
  });

  // 标签位置（轴端再外推一点）
  const LABEL_PUSH = 16;
  const labels = abilities.map((a, i) => {
    const ang = angleOf(i);
    const lx = cx + (R + LABEL_PUSH) * Math.cos(ang);
    const ly = cy + (R + LABEL_PUSH) * Math.sin(ang);
    // 文字对齐 — 12 点钟附近 middle，右侧 start，左侧 end
    const cosA = Math.cos(ang);
    let anchor = 'middle';
    if (cosA > 0.3) anchor = 'start';
    else if (cosA < -0.3) anchor = 'end';
    return { x: lx, y: ly, anchor, text: a.name };
  });

  return (
    <div className="v5r-radar-wrap" ref={wrapRef}>
      <svg width={width} height={HEIGHT} role="img" aria-label="八力雷达图">
        {/* 同心环 */}
        {rings.map((ring, i) => (
          <polygon
            key={i}
            points={ring.pts}
            fill="none"
            stroke="#cfdaef"
            strokeDasharray="2 4"
            strokeWidth="1"
          />
        ))}
        {/* 轴线 */}
        {spokes.map((s, i) => (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={s.x}
            y2={s.y}
            stroke="#cfdaef"
            strokeWidth="1"
          />
        ))}
        {/* 数据 polygon */}
        <polygon
          points={dataPts}
          fill="#1f53e6"
          fillOpacity="0.16"
          stroke="#1f53e6"
          strokeWidth="1.8"
        />
        {/* 数据点（小圆） */}
        {dataDots.map((d, i) => (
          <circle
            key={i}
            cx={d.cx}
            cy={d.cy}
            r="3.2"
            fill="#1f53e6"
          />
        ))}
        {/* 标签 */}
        {labels.map((l, i) => (
          <text
            key={i}
            x={l.x}
            y={l.y}
            textAnchor={l.anchor}
            dominantBaseline="middle"
            fontSize="12"
            fill="#26314f"
          >
            {l.text}
          </text>
        ))}
      </svg>
    </div>
  );
}
